import { ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ScheduledReportsSftpSettingsModalComponent } from 'dashboard/components/content-dashboard/scheduled-reports-dashboard-widget/modals';
import { ScheduledReportSftpSetting } from 'dashboard/interfaces/scheduled-report-sftp-setting.interface';
import { filter, tap } from 'rxjs';
import { ModalService, ToastService } from 'shared/services';

@Component({
    selector: 'app-scheduled-reports-sftp-control',
    templateUrl: './scheduled-reports-sftp-control.component.html',
    styleUrl: './scheduled-reports-sftp-control.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ScheduledReportsSftpControlComponent,
            multi: true,
        },
    ],
    imports: [TranslateModule],
})
export class ScheduledReportsSftpControlComponent implements ControlValueAccessor, OnChanges {
    @Input() sftpData: ScheduledReportSftpSetting[] = [];

    public sftpIds: any[] = [];
    public sftpIdNameMap: any = new Map<string, string>();

    private onChange!: Function;
    private onTouched!: Function;

    constructor(
        private modalService: ModalService,
        private destroyRef: DestroyRef,
        private changeDetectorRef: ChangeDetectorRef,
        private toastService: ToastService,
    ) {}

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.sftpData) {
            this.buildSftpIdMap(changes.sftpData.currentValue);
        }
    }

    public writeValue(sftpIds: string[]): void {
        this.sftpIds = sftpIds;
    }

    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    private buildSftpIdMap(sftpData: ScheduledReportSftpSetting[]) {
        this.sftpIdNameMap.clear();

        sftpData.forEach((sftp: ScheduledReportSftpSetting) => {
            this.sftpIdNameMap.set(sftp.sftpSettingId, sftp.siteName);
        });
    }

    public openSFTPSettingsModal(id?: any) {
        this.modalService
            .openModal(ScheduledReportsSftpSettingsModalComponent, { type: 'center', id: id, sftpData: this.sftpData })
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => this.onTouched()),
                filter(Boolean),
            )
            .subscribe((result) => {
                if (!id) {
                    if (this.sftpIds.includes(result.id)) {
                        this.toastService.showToast('dashboard.scheduledReports.sftpSettings.siteName.duplicate', 'error');
                        return;
                    }

                    this.sftpIds.push(result.id);

                    if (this.sftpData.findIndex((sftp: ScheduledReportSftpSetting) => sftp.sftpSettingId === result.id) === -1) {
                        this.sftpData.push({ sftpSettingId: result.id, siteName: result.name }); //sftpSettingId / siteName
                        this.sftpIdNameMap.set(result.id, result.name);
                    }
                } else {
                    const sftpData = this.sftpData.find((sftp: ScheduledReportSftpSetting) => sftp.sftpSettingId === id);

                    if (sftpData) {
                        this.sftpIdNameMap.set(result.id, result.name);
                        sftpData.siteName = result.name;
                        this.sftpIds[this.sftpIds.indexOf(id)] = result.id;
                    }
                }

                this.onTouched();
                this.onChange(this.sftpIds);
                this.changeDetectorRef.detectChanges();
            });
    }

    public deleteSftp(id: string) {
        const index = this.sftpIds.findIndex((sftpId: string) => sftpId === id);

        this.modalService
            .openDeleteConfirmationModal(
                undefined,
                undefined,
                undefined,
                this.sftpIdNameMap.get(id),
                'dashboard.scheduledReports.sftpSettings.siteName.label',
            )
            .onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
            .subscribe(() => {
                this.sftpIds.splice(index, 1);
                this.onTouched();
                this.onChange(this.sftpIds);
            });
    }
}
